/**
 * The join screen — the fifteen seconds that decide whether the product works.
 *
 * Someone has just tapped a link in WhatsApp with no idea what Beeline is. One sentence of
 * explanation, one optional field, one big button. Permission onboarding follows (milestone 4).
 */

import { SESSION, type SessionStatusResponse } from '@beeline/shared';

import { api, ApiError } from '../../lib/api';
import { el } from '../../lib/dom';
import { rememberMembership } from '../../lib/membership';
import { markLogo } from '../../ui/mark';

export interface JoinScreenOptions {
  code: string;
  status: SessionStatusResponse;
  /** Called once the slot is claimed, so the session view can move on. */
  onJoined: () => void;
  /** Called when the session turned out to be over or full while we were looking at it. */
  onUnavailable: (error: ApiError) => void;
}

export function joinScreen(options: JoinScreenOptions): HTMLElement {
  const hostName = options.status.host?.name;

  const nameInput = el('input', {
    class: 'field__input',
    id: 'join-name',
    type: 'text',
    name: 'name',
    autocomplete: 'given-name',
    enterkeyhint: 'go',
    maxlength: String(SESSION.maxNameLength),
    placeholder: 'Optional',
  });

  const button = el(
    'button',
    { class: 'button button--primary button--block', type: 'submit' },
    hostName ? `Find ${hostName}` : 'Find them',
  );

  const error = el('p', { class: 'form__error', role: 'alert' });

  let submitting = false;
  const submit = async (event: Event): Promise<void> => {
    event.preventDefault();
    if (submitting) return;

    submitting = true;
    button.setAttribute('disabled', '');
    button.textContent = 'Joining…';
    error.textContent = '';

    try {
      const joined = await api.joinSession(options.code, nameInput.value);
      rememberMembership({
        code: joined.code,
        participant: joined.participant,
        token: joined.token,
        name: nameInput.value.trim() || null,
        expiresAt: joined.expiresAt,
        created: false,
      });
      options.onJoined();
    } catch (caught) {
      submitting = false;
      button.removeAttribute('disabled');
      button.textContent = hostName ? `Find ${hostName}` : 'Find them';

      if (caught instanceof ApiError && caught.isTerminal) {
        options.onUnavailable(caught);
        return;
      }
      error.textContent =
        caught instanceof ApiError ? caught.message : 'Could not join. Try again.';
    }
  };

  return el(
    'main',
    { class: 'screen screen--centered join' },
    markLogo(52),
    el('h1', {
      class: 'screen__title',
      text: hostName ? `${hostName} wants to find you` : 'Someone wants to find you',
    }),
    el('p', {
      class: 'screen__body',
      text: 'Beeline shows you both a live arrow pointing at each other. No map, no app, no account.',
    }),

    el(
      'form',
      { class: 'form', onsubmit: (event: Event) => void submit(event) },
      el(
        'label',
        { class: 'field' },
        el('span', { class: 'field__label', text: 'Your first name' }),
        nameInput,
        el('span', {
          class: 'field__hint',
          text: 'Only used to write "Maria is 220 m away". Never stored.',
        }),
      ),
      error,
      button,
    ),
  );
}
